import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, MessageCircle, Plane, Play, X } from "lucide-react";
import { DRONES, waCotizarDron, waLink, track } from "@/data/content";
import { Reveal, TituloLineas, NumeroContador, FotoRevelada, Flotante, useInViewSafe, useReduceMotion } from "@/components/Reveal";
import MediaDrone from "@/components/MediaDrone";
import DroneTabs from "@/components/DroneTabs";

const META_DESC = {
  t100: "DJI Agras T100 en Bolivia: pulveriza con 100 L, siembra al voleo, fertiliza y transporta hasta 100 kg. Rinde de 30 a 40 hectáreas por hora en campo. Datos reales de CERTERO SRL. Cotice por WhatsApp.",
  t70p: "DJI Agras T70P en Bolivia: 70 litros de producto, hasta 30 hectáreas por hora y elevación de 65 kg. Datos de campo reales de CERTERO SRL. Cotice por WhatsApp.",
  t55: "DJI Agras T55 en Bolivia: el Agras más compacto, con 50 litros y hasta 30 hectáreas por hora. Datos de campo reales de CERTERO SRL. Cotice por WhatsApp.",
  "mavic-3m": "DJI Mavic 3M en Bolivia: mapeo multiespectral con RTK, 200 hectáreas por vuelo y mapas NDVI en 24 horas. Datos reales de CERTERO SRL. Cotice por WhatsApp.",
};

function Lightbox({ items, idx, onClose, onNav }) {
  const x0 = useRef(null);
  const item = items[idx];

  useEffect(() => {
    const f = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", f);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", f);
      document.body.style.overflow = "";
    };
  }, [onClose, onNav]);

  return (
    <div
      data-testid="lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[110] flex flex-col items-center justify-center bg-ink/95 p-4 backdrop-blur-sm"
    >
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        aria-label="Cerrar galería"
        className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-full border border-hud px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-mist transition-colors duration-300 hover:border-certero hover:text-certero"
      >
        Cerrar <X className="h-4 w-4" />
      </button>

      <button
        data-testid="lightbox-prev"
        onClick={(e) => { e.stopPropagation(); onNav(-1); }}
        aria-label="Foto anterior"
        className="absolute left-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-hud bg-abyss/80 text-mist transition-colors duration-300 hover:border-certero hover:text-certero sm:left-6"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        data-testid="lightbox-next"
        onClick={(e) => { e.stopPropagation(); onNav(1); }}
        aria-label="Foto siguiente"
        className="absolute right-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-hud bg-abyss/80 text-mist transition-colors duration-300 hover:border-certero hover:text-certero sm:right-6"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => { x0.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (x0.current === null) return;
          const dx = e.changedTouches[0].clientX - x0.current;
          if (Math.abs(dx) > 48) onNav(dx < 0 ? 1 : -1);
          x0.current = null;
        }}
        className="flex max-w-full flex-col items-center"
        data-testid="lightbox-contenido"
      >
        {item.video ? (
          <video src={item.video} controls autoPlay playsInline className="max-h-[72vh] w-auto max-w-full border border-hud" />
        ) : (
          <img
            src={item.src}
            alt={item.alt || "Foto del dron en operación"}
            className="max-h-[72vh] w-auto max-w-full border border-hud object-contain"
          />
        )}
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">{item.credito}</p>
      </div>

      <p data-testid="lightbox-contador" className="mt-2 font-mono text-[10px] tracking-[0.25em] text-smoke">
        {idx + 1} / {items.length}
      </p>
    </div>
  );
}

// Portada viva: video propio del dron en bucle, sin sonido, solo cuando
// está en pantalla. Con «reducir movimiento»: la foto fija.
function PortadaVideo({ drone }) {
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const inView = useInView(wrapRef, { margin: "240px 0px 240px 0px" });
  const reduce = useReduceMotion();

  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    if (inView && !reduce) v.play().catch(() => {});
    else v.pause();
  }, [inView, reduce]);

  return (
    <div ref={wrapRef} className="relative overflow-hidden border border-hud bg-ink" data-testid={`drone-portada-${drone.slug}`}>
      {reduce ? (
        <img
          src={drone.portada}
          alt={`${drone.modelo}: ${drone.alt}`}
          loading="eager"
          decoding="async"
          className="aspect-[16/9] w-full object-cover"
        />
      ) : (
        <video
          ref={vidRef}
          data-testid={`drone-portada-video-${drone.slug}`}
          src={drone.video}
          poster={drone.portada}
          muted
          loop
          playsInline
          preload="metadata"
          aria-label={`${drone.modelo} en funcionamiento`}
          className="aspect-[16/9] w-full object-cover"
        />
      )}
      <span className="absolute left-4 top-4 border border-certero/60 bg-ink/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-certero backdrop-blur-sm">
        {drone.tag}
      </span>
    </div>
  );
}

// Franja a todo el ancho entre las pestañas y la galería: el video del dron
// trabajando, o su foto/recorte cuando no hay video propio.
function FranjaTrabajo({ drone }) {
  const franja = drone.franja;
  const esMedia = franja.tipo === "video" || franja.tipo === "foto";
  return (
    <Reveal className="mt-12">
      <div data-testid={`franja-trabajo-${drone.slug}`} className="relative overflow-hidden border border-hud bg-ink">
        {franja.tipo === "video" && <VideoFranja drone={drone} franja={franja} />}
        {franja.tipo === "foto" && (
          <img
            src={franja.src}
            alt={franja.alt}
            loading="lazy"
            decoding="async"
            className="aspect-[16/9] w-full object-cover sm:aspect-[21/9]"
          />
        )}
        {franja.tipo === "recorte" && (
          <div className="relative flex aspect-[16/9] w-full items-center justify-center overflow-hidden bg-[#0c295c]/60 pb-10 sm:aspect-[21/9] sm:pb-14">
            <span
              className="pointer-events-none absolute inset-0 opacity-[0.07]"
              style={{ background: "radial-gradient(circle at 50% 120%, #52c234 0%, transparent 60%)" }}
              aria-hidden="true"
            />
            <Flotante className="flex w-full justify-center px-8">
              <img
                src={franja.src}
                alt={franja.alt}
                loading="lazy"
                decoding="async"
                className="max-h-40 w-auto max-w-[520px] drop-shadow-[0_18px_30px_rgba(0,0,0,0.45)] sm:max-h-56"
              />
            </Flotante>
          </div>
        )}
        {franja.tipo === "panel" && (
          <div className="relative flex aspect-[16/9] w-full items-center justify-center overflow-hidden bg-[#0c295c]/60 pb-10 sm:aspect-[21/9] sm:pb-14">
            <span
              className="pointer-events-none absolute inset-0 opacity-[0.07]"
              style={{ background: "radial-gradient(circle at 50% 120%, #52c234 0%, transparent 60%)" }}
              aria-hidden="true"
            />
            <img src="/logo-color.svg" alt="" aria-hidden="true" className="h-12 w-auto opacity-90 sm:h-14" loading="lazy" />
          </div>
        )}
        <div
          className={`pointer-events-none absolute inset-0 flex items-end bg-gradient-to-t from-ink/90 via-ink/25 to-transparent ${
            esMedia ? "justify-start" : "justify-center"
          }`}
        >
          <p
            className={`p-6 font-display text-2xl font-extrabold uppercase tracking-tight text-mist drop-shadow-[0_2px_12px_rgba(0,0,0,0.65)] sm:text-3xl lg:text-4xl ${
              esMedia ? "" : "text-center"
            }`}
          >
            Así trabaja el {drone.modelo.replace("DJI ", "")}
          </p>
        </div>
      </div>
    </Reveal>
  );
}

function VideoFranja({ drone, franja }) {
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const inView = useInView(wrapRef, { margin: "240px 0px 240px 0px" });
  const reduce = useReduceMotion();

  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    if (inView && !reduce) v.play().catch(() => {});
    else v.pause();
  }, [inView, reduce]);

  return (
    <div ref={wrapRef}>
      {reduce ? (
        <img
          src={franja.poster || drone.portada}
          alt={`${drone.modelo} trabajando en campo`}
          loading="lazy"
          decoding="async"
          className="aspect-[16/9] w-full object-cover sm:aspect-[21/9]"
        />
      ) : (
        <video
          ref={vidRef}
          data-testid={`franja-video-${drone.slug}`}
          src={franja.src}
          poster={franja.poster || drone.portada}
          muted
          loop
          playsInline
          preload="none"
          aria-label={`${drone.modelo} trabajando en campo`}
          className="aspect-[16/9] w-full object-cover sm:aspect-[21/9]"
        />
      )}
    </div>
  );
}

// Galería: en computadora, grilla con acercamiento suave al pasar el mouse;
// en celular, carrusel horizontal para deslizar con el dedo, con puntos que
// muestran en qué foto va.
function Galeria({ drone, onOpen }) {
  const scroller = useRef(null);
  const [idx, setIdx] = useState(0);

  const onScroll = () => {
    const el = scroller.current;
    if (!el || !el.clientWidth) return;
    setIdx(Math.max(0, Math.min(drone.galeria.length - 1, Math.round(el.scrollLeft / el.clientWidth))));
  };
  const ir = (i) => {
    const el = scroller.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div className="mt-6">
      <div className="md:hidden">
        <div
          ref={scroller}
          onScroll={onScroll}
          data-testid={`galeria-carrusel-${drone.slug}`}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {drone.galeria.map((g, i) => (
            <button
              key={g.src || g.video}
              data-testid={`galeria-carrusel-foto-${drone.slug}-${i}`}
              onClick={() => { track("galeria_click", { modelo: drone.modelo, indice: i, origen: "carrusel" }); onOpen(i); }}
              className="w-[82%] shrink-0 snap-center overflow-hidden border border-hud p-0 text-left"
              aria-label={`Abrir foto ${i + 1} de la galería del ${drone.modelo}`}
            >
              <GaleriaMedia g={g} drone={drone} i={i} />
            </button>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-center gap-2">
          {drone.galeria.map((_, i) => (
            <button
              key={i}
              data-testid={`galeria-punto-${drone.slug}-${i}`}
              onClick={() => ir(i)}
              aria-label={`Ir a la foto ${i + 1} de la galería del ${drone.modelo}`}
              className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-certero" : "w-2 bg-mist/30 hover:bg-mist/60"}`}
            />
          ))}
        </div>
      </div>

      <div className="hidden gap-4 md:grid md:grid-cols-2 lg:grid-cols-3" data-testid={`galeria-${drone.slug}`}>
        {drone.galeria.map((g, i) => (
          <Reveal key={g.src || g.video} delay={Math.min(0.3, 0.06 * i)}>
            <button
              data-testid={`galeria-foto-${drone.slug}-${i}`}
              onClick={() => { track("galeria_click", { modelo: drone.modelo, indice: i }); onOpen(i); }}
              className="group relative block w-full overflow-hidden border border-hud p-0 transition-colors duration-300 hover:border-certero/60"
              aria-label={`Abrir foto ${i + 1} de la galería del ${drone.modelo}`}
            >
              <GaleriaMedia g={g} drone={drone} i={i} hover />
            </button>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

function GaleriaMedia({ g, drone, i, hover = false }) {
  const zoom = hover ? "transition-transform duration-500 group-hover:scale-[1.04]" : "";
  if (g.video) {
    return (
      <span className={`relative block aspect-[4/3] w-full overflow-hidden ${zoom}`}>
        <video src={g.video} muted playsInline preload="metadata" className="h-full w-full object-cover" />
        <span className="absolute inset-0 flex items-center justify-center bg-ink/40">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-certero text-ink">
            <Play className="ml-0.5 h-4 w-4 fill-current" />
          </span>
        </span>
      </span>
    );
  }
  return (
    <img
      src={g.src}
      alt={g.alt || `${drone.modelo} — foto ${i + 1}`}
      loading="lazy"
      decoding="async"
      className={`aspect-[4/3] w-full object-cover ${zoom}`}
    />
  );
}

// Barra fija inferior al bajar por la página: cotización directa por WhatsApp.
// Desaparece al llegar al bloque de cierre.
function BarraCotizar({ drone, cierreRef }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const cierre = cierreRef?.current;
      const enCierre = cierre ? cierre.getBoundingClientRect().top < window.innerHeight * 0.9 : false;
      setVisible(window.scrollY > 520 && !enCierre);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [cierreRef]);

  useEffect(() => {
    document.body.classList.toggle("con-barra-cotizar", visible);
    return () => document.body.classList.remove("con-barra-cotizar");
  }, [visible]);

  return (
    <div
      data-testid={`barra-cotizar-${drone.slug}`}
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-[80] border-t border-hud bg-ink/90 backdrop-blur-md transition-transform duration-300 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 pt-3 sm:px-8 lg:px-12">
        <div className="min-w-0">
          <p className="truncate font-display text-sm font-bold uppercase tracking-wide text-mist">{drone.modelo}</p>
          <p className="hidden truncate whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.2em] text-smoke sm:block">
            Cotización sin compromiso
          </p>
        </div>
        <a
          data-testid={`barra-btn-whatsapp-${drone.slug}`}
          href={waCotizarDron(drone.modelo)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("cotizar_dron_whatsapp", { modelo: drone.modelo, origen: "barra_fija" })}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-certero px-6 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-[#4bc443]"
        >
          <MessageCircle className="h-4 w-4" /> Cotizar este dron
        </a>
      </div>
    </div>
  );
}

export default function DronePage() {
  const { slug } = useParams();
  const drone = DRONES.find((d) => d.slug === slug);
  const [lb, setLb] = useState(null);
  const cierreRef = useRef(null);

  useEffect(() => {
    if (!drone) return;
    const prev = { t: document.title, d: document.querySelector('meta[name="description"]')?.content, c: document.querySelector('link[rel="canonical"]')?.href };
    document.title = `${drone.modelo} en Bolivia | CERTERO SRL`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.content = META_DESC[drone.slug] || drone.paraQue;
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = `https://certerosrl.com/drones/${drone.slug}`;
    return () => {
      document.title = prev.t;
      if (meta && prev.d) meta.content = prev.d;
      if (link && prev.c) link.href = prev.c;
    };
  }, [drone]);

  if (!drone) return <Navigate to="/" replace />;

  const nav = (delta) => setLb((v) => (v === null ? v : (v + delta + drone.galeria.length) % drone.galeria.length));

  return (
    <div className="bg-abyss px-4 pb-24 pt-28 sm:px-8 lg:px-12 lg:pt-32">
      <div className="mx-auto max-w-[1400px]" data-testid="drone-page">
        {/* Ruta para volver */}
        <nav aria-label="Ruta de navegación" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke" data-testid="drone-breadcrumb">
          <Link to="/" className="transition-colors duration-300 hover:text-certero">Inicio</Link>
          <span aria-hidden="true">›</span>
          <Link to="/#drones" className="transition-colors duration-300 hover:text-certero">Drones</Link>
          <span aria-hidden="true">›</span>
          <span className="text-certero">{drone.modelo}</span>
        </nav>

        {/* 1. Portada viva: video propio en bucle o foto con zoom documental */}
        <Reveal className="mt-8">
          {drone.video ? (
            <PortadaVideo drone={drone} />
          ) : drone.portada ? (
            <div className="relative overflow-hidden border border-hud bg-ink" data-testid={`drone-portada-foto-${drone.slug}`}>
              <FotoRevelada
                src={drone.portada}
                alt={`${drone.modelo}: ${drone.alt}`}
                kenburns
                className="aspect-[16/9] w-full"
                loading="eager"
              />
              <span className="absolute left-4 top-4 z-10 border border-certero/60 bg-ink/80 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-certero backdrop-blur-sm">
                {drone.tag}
              </span>
            </div>
          ) : (
            <MediaDrone drone={drone} />
          )}
        </Reveal>

        <div className="mt-10 grid gap-10 lg:grid-cols-12">
          <Reveal delay={0.06} className="lg:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">{drone.linea}</p>
            <TituloLineas
              as="h1"
              lineas={[drone.modelo]}
              className="mt-3 font-display text-4xl font-extrabold uppercase tracking-tight text-mist sm:text-5xl lg:text-6xl"
            />
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-mist/80 sm:text-base">{drone.paraQue}</p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <a
                data-testid={`page-btn-cotizar-${drone.slug}`}
                href={waCotizarDron(drone.modelo)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("cotizar_dron_whatsapp", { modelo: drone.modelo, origen: "pagina_dron" })}
                className="inline-flex h-12 items-center rounded-full bg-certero px-7 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,transform] duration-300 hover:bg-[#4bc443] active:scale-95"
              >
                Cotizar este dron
              </a>
              <a
                data-testid={`page-btn-ficha-${drone.slug}`}
                href={drone.brochure}
                target="_blank"
                rel="noopener noreferrer"
                download
                onClick={() => track("ficha_pdf_click", { modelo: drone.modelo })}
                className="inline-flex h-12 items-center gap-2 rounded-full border border-certero px-6 font-display text-xs font-bold uppercase tracking-[0.15em] text-certero transition-[background-color,color] duration-300 hover:bg-certero hover:text-ink"
              >
                <Download className="h-4 w-4" /> Descargar ficha técnica (PDF, {drone.brochureMB})
              </a>
            </div>
          </Reveal>

          {/* 2. Las cuatro cifras + vista frontal oficial (flotante) */}
          <div className={`grid items-center gap-8 ${drone.frente ? "lg:col-span-12 lg:grid-cols-12" : "lg:col-span-5"}`}>
            {drone.frente && (
              <Reveal delay={0.08} className="lg:col-span-4">
                <Flotante>
                  <img
                    src={drone.frente}
                    alt={`Vista frontal oficial del ${drone.modelo}`}
                    loading="lazy"
                    decoding="async"
                    className="mx-auto w-full max-w-[420px] drop-shadow-[0_18px_30px_rgba(0,0,0,0.45)]"
                    data-testid={`drone-frente-${drone.slug}`}
                  />
                </Flotante>
                <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-smoke/70">Imagen: DJI Agriculture</p>
              </Reveal>
            )}
            <div className={drone.frente ? "lg:col-span-8" : ""}>
              <div className={`grid h-full grid-cols-2 gap-px border border-hud bg-hud/40 ${drone.frente ? "" : "lg:grid-cols-4"}`}>
                {drone.bento.map((b, i) => (
                  <Reveal key={b.l} delay={0.08 + i * 0.1} className="flex">
                    <div data-testid={`drone-stat-${drone.slug}-${i}`} className="flex w-full flex-col justify-center bg-abyss p-6 sm:p-8">
                      <NumeroContador texto={b.n} className="font-mono text-3xl font-extrabold tracking-tight text-certero sm:text-4xl xl:text-5xl" />
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-smoke sm:text-[11px]">{b.l}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 3. Pestañas */}
        <Reveal className="mt-12">
          <DroneTabs drone={drone} />
        </Reveal>

        {/* 4. Franja: así trabaja el dron (solo si tiene video propio) */}
        {drone.franja && <FranjaTrabajo drone={drone} />}

        {/* 5. Galería (solo si tiene fotos) */}
        {drone.galeria.length > 0 && (
          <div className="mt-14">
            <Reveal>
              <h2 className="font-display text-xl font-bold uppercase tracking-wide text-mist sm:text-2xl">
                El {drone.modelo.replace("DJI ", "")} en detalle
              </h2>
              <p className="mt-2 text-sm text-smoke">Toque una foto para verla en grande; pase a la siguiente con flechas o con el dedo.</p>
            </Reveal>
            <Galeria drone={drone} onOpen={setLb} />
          </div>
        )}

        {/* 6. Cierre */}
        <div ref={cierreRef}>
          <Reveal className="mt-16">
            <div data-testid={`drone-cierre-${drone.slug}`} className="flex flex-col items-start gap-6 border border-certero/40 bg-[#0c295c]/40 p-8 sm:p-10 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-display text-xl font-bold uppercase tracking-wide text-mist sm:text-2xl">
                ¿Quiere verlo trabajar en su campo?
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-smoke">
                Coordinamos una demostración con el {drone.modelo} en su cultivo, sin compromiso.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <a
                data-testid={`page-btn-cierre-cotizar-${drone.slug}`}
                href={waCotizarDron(drone.modelo)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-12 items-center rounded-full bg-certero px-7 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,transform] duration-300 hover:bg-[#4bc443] active:scale-95"
              >
                Cotizar este dron
              </a>
              <a
                data-testid={`page-btn-demo-${drone.slug}`}
                href={waLink(`Hola CERTERO SRL, quiero reservar una demostración en campo con el ${drone.modelo}.`)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("reservar_demo_whatsapp", { modelo: drone.modelo, origen: "pagina_dron" })}
                className="inline-flex h-12 items-center gap-2 rounded-full border border-certero px-6 font-display text-xs font-bold uppercase tracking-[0.15em] text-certero transition-[background-color,color] duration-300 hover:bg-certero hover:text-ink"
              >
                <Plane className="h-4 w-4" /> Reservar demo en campo
              </a>
            </div>
          </div>
        </Reveal>
        </div>
      </div>

      <BarraCotizar drone={drone} cierreRef={cierreRef} />
      {lb !== null && <Lightbox items={drone.galeria} idx={lb} onClose={() => setLb(null)} onNav={nav} />}
    </div>
  );
}
